import { CATEGORIES } from "../../constants/categories";

const PAPER_BG = "linear-gradient(180deg, #FDF6E3 0%, #F5E6C8 100%)";
const PAPER_BG_DARK = "linear-gradient(180deg, #2A2520 0%, #1E1A16 100%)";
const LINE_COLOR = "rgba(180, 160, 130, 0.3)";
const LINE_COLOR_DARK = "rgba(180, 160, 130, 0.15)";
const INK_COLOR = "#3C2A14";
const INK_COLOR_DARK = "#D4C4A8";
const INK_MUTED = "#8B7355";
const INK_MUTED_DARK = "#9B8B75";
const ACCENT = "#C0392B";

const isDark = () => document.documentElement.getAttribute("data-theme") !== "light";

function JournalEntry({ entry, index }) {
  const dark = isDark();
  const date = new Date(entry.date + "T00:00:00");
  const dayNames = ["Chủ Nhật", "Thứ Hai", "Thứ Ba", "Thứ Tư", "Thứ Năm", "Thứ Sáu", "Thứ Bảy"];
  const monthNames = ["tháng 1", "tháng 2", "tháng 3", "tháng 4", "tháng 5", "tháng 6",
    "tháng 7", "tháng 8", "tháng 9", "tháng 10", "tháng 11", "tháng 12"];
  const dayName = dayNames[date.getDay()];
  const dateStr = `${dayName}, ${date.getDate()} ${monthNames[date.getMonth()]} ${date.getFullYear()}`;
  const cat = CATEGORIES.find(c => c.id === entry.category_id) || CATEGORIES[0];

  return (
    <div style={{
      background: dark ? PAPER_BG_DARK : PAPER_BG,
      borderRadius: "12px",
      padding: "28px 32px",
      marginBottom: "20px",
      position: "relative",
      boxShadow: dark
        ? "0 2px 12px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.03)"
        : "0 2px 12px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.5)",
      borderLeft: `4px solid ${cat.color || ACCENT}`,
      animation: `fadeInUp 0.4s ease ${index * 0.05}s both`,
    }}>
      {/* Lined paper effect */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
        backgroundImage: `repeating-linear-gradient(
          transparent, transparent 31px,
          ${dark ? LINE_COLOR_DARK : LINE_COLOR} 31px,
          ${dark ? LINE_COLOR_DARK : LINE_COLOR} 32px
        )`,
        backgroundPosition: "0 20px",
        borderRadius: "12px",
        pointerEvents: "none",
        opacity: 0.7,
      }} />

      {/* Date header */}
      <div style={{
        fontFamily: "'Georgia', 'Times New Roman', serif",
        fontSize: "13px",
        color: dark ? INK_MUTED_DARK : INK_MUTED,
        marginBottom: "12px",
        letterSpacing: "0.5px",
        position: "relative",
      }}>
        {dateStr}
      </div>

      {/* Challenge title */}
      <div style={{
        fontFamily: "'Georgia', 'Times New Roman', serif",
        fontSize: "20px",
        fontWeight: "700",
        color: dark ? INK_COLOR_DARK : INK_COLOR,
        marginBottom: "8px",
        lineHeight: "1.4",
        position: "relative",
      }}>
        {cat.emoji} {entry.title}
      </div>

      {/* Category & time */}
      <div style={{
        fontSize: "12px",
        color: dark ? INK_MUTED_DARK : INK_MUTED,
        marginBottom: "16px",
        fontFamily: "'Georgia', serif",
        fontStyle: "italic",
        position: "relative",
      }}>
        {cat.label} · {entry.time_required || "2 phút"}
      </div>

      {/* Reflection - user's own words */}
      {entry.reflection && (
        <div style={{
          fontFamily: "'Georgia', 'Times New Roman', serif",
          fontSize: "15px",
          lineHeight: "2",
          color: dark ? INK_COLOR_DARK : INK_COLOR,
          position: "relative",
          marginBottom: entry.ai_feedback ? "16px" : "0",
          whiteSpace: "pre-wrap",
        }}>
          <span style={{
            fontSize: "24px",
            fontFamily: "'Georgia', serif",
            color: cat.color || ACCENT,
            fontWeight: "bold",
            lineHeight: "1",
          }}>"</span>
          {entry.reflection}
          <span style={{
            fontSize: "24px",
            fontFamily: "'Georgia', serif",
            color: cat.color || ACCENT,
            fontWeight: "bold",
            lineHeight: "1",
          }}>"</span>
        </div>
      )}

      {/* AI feedback */}
      {entry.ai_feedback && (
        <div style={{
          fontFamily: "'Georgia', serif",
          fontSize: "13px",
          lineHeight: "1.8",
          color: dark ? "rgba(200,180,140,0.7)" : "rgba(100,80,50,0.6)",
          position: "relative",
          paddingLeft: "16px",
          borderLeft: `2px solid ${dark ? "rgba(200,180,140,0.2)" : "rgba(100,80,50,0.15)"}`,
          fontStyle: "italic",
        }}>
          {entry.ai_feedback}
        </div>
      )}
    </div>
  );
}

export default function JournalStyle({
  history, completedCount, streak,
}) {
  const dark = isDark();

  // Only show entries that have reflections, sorted newest first
  const entriesWithReflection = history
    .filter(h => h.completed && h.reflection)
    .sort((a, b) => b.date.localeCompare(a.date));

  const allCompleted = history
    .filter(h => h.completed)
    .sort((a, b) => b.date.localeCompare(a.date));

  // Group by month for section headers
  const grouped = {};
  const entries = entriesWithReflection.length > 0 ? entriesWithReflection : allCompleted;
  entries.forEach(e => {
    const d = new Date(e.date + "T00:00:00");
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(e);
  });

  const monthLabels = ["Tháng 1", "Tháng 2", "Tháng 3", "Tháng 4", "Tháng 5", "Tháng 6",
    "Tháng 7", "Tháng 8", "Tháng 9", "Tháng 10", "Tháng 11", "Tháng 12"];

  return (
    <div style={{ maxWidth: "640px", margin: "0 auto" }}>
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Journal cover */}
      <div style={{
        textAlign: "center",
        marginBottom: "32px",
        animation: "fadeInUp 0.4s ease both",
      }}>
        <div style={{
          fontSize: "40px",
          marginBottom: "8px",
        }}>📖</div>
        <div style={{
          fontFamily: "'Georgia', serif",
          fontSize: "24px",
          fontWeight: "700",
          color: dark ? INK_COLOR_DARK : INK_COLOR,
          marginBottom: "4px",
        }}>
          Nhật Ký Hành Trình
        </div>
        <div style={{
          fontFamily: "'Georgia', serif",
          fontSize: "14px",
          fontStyle: "italic",
          color: dark ? INK_MUTED_DARK : INK_MUTED,
        }}>
          {completedCount} ngày đã ghi · Streak: {streak.current_streak} ngày
        </div>
      </div>

      {/* Entries */}
      {Object.keys(grouped).length === 0 ? (
        <div style={{
          textAlign: "center",
          padding: "48px 24px",
          fontFamily: "'Georgia', serif",
          color: dark ? INK_MUTED_DARK : INK_MUTED,
          fontStyle: "italic",
          fontSize: "15px",
        }}>
          Chưa có trang nhật ký nào.
          <br />
          Hoàn thành thử thách và viết cảm nhận để bắt đầu cuốn nhật ký của bạn.
        </div>
      ) : (
        Object.keys(grouped).sort((a, b) => b.localeCompare(a)).map(monthKey => {
          const [year, month] = monthKey.split("-");
          const label = `${monthLabels[parseInt(month) - 1]} ${year}`;
          let entryIndex = 0;
          return (
            <div key={monthKey} style={{ marginBottom: "32px" }}>
              {/* Month divider */}
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
                marginBottom: "20px",
              }}>
                <div style={{
                  flex: 1,
                  height: "1px",
                  background: dark ? "rgba(200,180,140,0.15)" : "rgba(100,80,50,0.15)",
                }} />
                <div style={{
                  fontFamily: "'Georgia', serif",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: dark ? INK_MUTED_DARK : INK_MUTED,
                  letterSpacing: "1px",
                  textTransform: "uppercase",
                }}>
                  {label}
                </div>
                <div style={{
                  flex: 1,
                  height: "1px",
                  background: dark ? "rgba(200,180,140,0.15)" : "rgba(100,80,50,0.15)",
                }} />
              </div>

              {/* Entries for this month */}
              {grouped[monthKey].map(entry => (
                <JournalEntry key={entry.id} entry={entry} index={entryIndex++} />
              ))}
            </div>
          );
        })
      )}

      {/* Footer quote */}
      {entries.length > 0 && (
        <div style={{
          textAlign: "center",
          padding: "24px",
          fontFamily: "'Georgia', serif",
          fontSize: "14px",
          fontStyle: "italic",
          color: dark ? "rgba(200,180,140,0.4)" : "rgba(100,80,50,0.3)",
          animation: "fadeInUp 0.4s ease 0.3s both",
        }}>
          "Cuộc sống không được đo bằng số hơi thở, mà bằng những khoảnh khắc làm ta nín thở."
        </div>
      )}
    </div>
  );
}
